import {GitHubAvatar} from '@github-ui/github-avatar'
import {useQuery} from '@github-ui/react-query'
import {testIdProps} from '@github-ui/test-id-props'
import {useDebounce} from '@github-ui/use-debounce'
import {CheckIcon, DashIcon, OrganizationIcon, PersonIcon, TriangleDownIcon} from '@primer/octicons-react'
import {
  Button,
  Checkbox,
  /* eslint-disable-next-line primer-react/no-deprecated-flash -- Existing Flash usage should be migrated to Banner. */
  Flash,
  FormControl,
  SelectPanel,
  type SelectPanelItemInput,
  TextInput,
  VisuallyHidden,
} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {clsx} from 'clsx'
import {type HTMLAttributes, useEffect, useMemo, useRef, useState} from 'react'

import {copyProjectOwnersQueryOptions} from '../../queries/copy-project-owners'
import styles from './CopyProjectDialog.module.css'
import {navigateToHtmlUrl} from './navigate-to-html-url'
import type {CopyProjectOwner, Project} from './types'
import {useCopyProject} from './use-copy-project'

export type CopyProjectDialogMode = 'copy' | 'template'

interface CopyProjectDialogProps {
  project: Project
  mode: CopyProjectDialogMode
  onClose: () => void
}

const COPY_TITLE_PREFIX = '[COPY]'
const TEMPLATE_TITLE_PREFIX = '[TEMPLATE]'
const OWNER_SEARCH_DEBOUNCE_MS = 300
const OWNER_ITEM_ID_PREFIX = 'owner-'
const OWNER_REQUIRED_HINT_ID = 'copy-project-owner-required-hint'
const OWNER_LABEL_ID = 'copy-project-owner-label'
const OWNER_VALUE_ID = 'copy-project-owner-value'
const OWNER_ERROR_ID = 'copy-project-owner-error'

function buildOwnerItemId(login: string): string {
  return `${OWNER_ITEM_ID_PREFIX}${login}`
}

function parseOwnerItemId(id: unknown): string | null {
  if (typeof id !== 'string' || !id.startsWith(OWNER_ITEM_ID_PREFIX)) {
    return null
  }
  return id.slice(OWNER_ITEM_ID_PREFIX.length)
}

function buildOwnerItem(owner: CopyProjectOwner): SelectPanelItemInput {
  return {
    id: buildOwnerItemId(owner.login),
    text: owner.login,
    ...(owner.name ? {description: owner.name, descriptionVariant: 'block' as const} : {}),
    leadingVisual: () => <GitHubAvatar src={owner.avatarUrl} alt="" size={20} square={owner.isOrganization} />,
    trailingVisual: () =>
      owner.isOrganization ? <OrganizationIcon aria-hidden="true" /> : <PersonIcon aria-hidden="true" />,
  }
}

export function CopyProjectDialog({project, mode, onClose}: CopyProjectDialogProps) {
  const isTemplateMode = mode === 'template'
  const dialogTitle = isTemplateMode ? 'Copy as template' : 'Make a copy'
  const submitLabel = isTemplateMode ? 'Copy as template' : 'Copy project'

  const [inputValue, setInputValue] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [ownerPickerOpen, setOwnerPickerOpen] = useState(false)

  const ownersQuery = useQuery(
    copyProjectOwnersQueryOptions({projectId: project.id, copyAsTemplate: isTemplateMode, q: debouncedQuery}),
  )
  const owners = ownersQuery.data?.owners ?? EMPTY_OWNERS

  const [selectedOwner, setSelectedOwner] = useState<CopyProjectOwner | null>(null)
  if (selectedOwner === null && debouncedQuery.trim() === '' && owners.length > 0) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    setSelectedOwner(owners[0]!)
  }

  const items = useMemo<SelectPanelItemInput[]>(() => owners.map(buildOwnerItem), [owners])
  const ownerByLogin = useMemo(() => {
    const lookup = new Map<string, CopyProjectOwner>()
    for (const owner of owners) {
      lookup.set(owner.login, owner)
    }
    return lookup
  }, [owners])

  const selectedItem = useMemo<SelectPanelItemInput | undefined>(
    () => (selectedOwner ? buildOwnerItem(selectedOwner) : undefined),
    [selectedOwner],
  )

  const debouncedSetQuery = useDebounce(setDebouncedQuery, OWNER_SEARCH_DEBOUNCE_MS)
  const handleFilterChange = (next: string) => {
    setInputValue(next)
    debouncedSetQuery(next)
  }

  const handleOwnerSelectedChange = (selected: SelectPanelItemInput | undefined) => {
    if (!selected) {
      return
    }
    const login = parseOwnerItemId(selected.id)
    const owner = login ? ownerByLogin.get(login) : undefined
    if (owner) {
      setSelectedOwner(owner)
    }
  }

  const ownerMessage = useMemo(() => {
    if (ownersQuery.isError) {
      return {variant: 'error' as const, title: "Couldn't load owners", body: 'Try again later.'}
    }
    if (!ownersQuery.isFetching && items.length === 0) {
      const hasQuery = debouncedQuery.trim() !== ''
      return {
        variant: 'empty' as const,
        title: hasQuery ? 'No owners matched' : 'No owners found',
        body: hasQuery ? 'Try a different search term.' : 'No owners are available.',
      }
    }
    return undefined
  }, [ownersQuery.isError, ownersQuery.isFetching, items.length, debouncedQuery])

  const [title, setTitle] = useState(() => buildDefaultTitle(project.title, mode))
  const [includeDraftIssues, setIncludeDraftIssues] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const trimmedTitle = title.trim()
  const titleIsValid = trimmedTitle.length > 0

  const copyMutation = useCopyProject({
    sourceOwnerLogin: project.owner,
    // Per-row, not page-level: a transferred-repo edge case can leave the
    // project's owner and the repo's owner pointing at different accounts, so
    // the URL segment must match the project's owner specifically.
    sourceOwnerIsOrganization: project.projectOwnerIsOrganization,
    sourceProjectNumber: project.number,
  })

  const submitInactive = !selectedOwner || !titleIsValid || copyMutation.isPending

  const abortControllerRef = useRef<AbortController | null>(null)

  const handleSubmit = async () => {
    // Footer buttons can be triggered by Enter from a focused text field even
    // when visually inactive, so re-check here to avoid duplicate submits.
    if (submitInactive) {
      return
    }
    // Clear any prior error state so the retry shows a fresh "Copying…" label
    // instead of carrying the previous failure into the new attempt.
    copyMutation.reset()
    setErrorMessage(null)
    const controller = new AbortController()
    abortControllerRef.current = controller
    try {
      const data = await copyMutation.mutateAsync({
        targetOwner: selectedOwner,
        title: trimmedTitle,
        mode,
        includeDraftIssues: !isTemplateMode && includeDraftIssues,
        signal: controller.signal,
      })
      // Close before navigating so the source page's overlay is gone by the
      // time the browser starts unloading.
      onClose()
      navigateToHtmlUrl(data.htmlUrl)
    } catch (error) {
      if (controller.signal.aborted) {
        return
      }
      setErrorMessage(error instanceof Error ? error.message : 'Something went wrong. Please try again.')
    }
  }

  const handleClose = () => {
    abortControllerRef.current?.abort()
    debouncedSetQuery.cancel()
    onClose()
  }

  const renderOwnerAnchor = <T extends HTMLAttributes<HTMLElement>>(anchorProps: T) => (
    <Button
      trailingAction={TriangleDownIcon}
      leadingVisual={
        selectedOwner
          ? () => <GitHubAvatar src={selectedOwner.avatarUrl} alt="" size={20} square={selectedOwner.isOrganization} />
          : undefined
      }
      {...anchorProps}
      aria-haspopup="dialog"
      aria-labelledby={`${OWNER_LABEL_ID} ${OWNER_VALUE_ID}`}
      aria-describedby={[
        anchorProps['aria-describedby'],
        OWNER_REQUIRED_HINT_ID,
        ownersQuery.isError ? OWNER_ERROR_ID : null,
      ]
        .filter(Boolean)
        .join(' ')}
      {...testIdProps('copy-project-owner-button')}
    >
      <span id={OWNER_VALUE_ID}>{selectedOwner ? selectedOwner.login : 'Select an owner'}</span>
    </Button>
  )

  // Focus and select the title field on mount so the user's first edit lands
  // there. The owner picker auto-selects, so it doesn't need initial focus.
  const titleInputRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    titleInputRef.current?.select()
  }, [])

  return (
    <Dialog
      title={dialogTitle}
      width="large"
      onClose={handleClose}
      footerButtons={[
        {buttonType: 'default', content: 'Cancel', onClick: handleClose},
        {
          buttonType: 'primary',
          content: copyMutation.isPending ? 'Copying…' : submitLabel,
          onClick: handleSubmit,
          inactive: submitInactive,
          autoFocus: false,
        },
      ]}
    >
      <div {...testIdProps(isTemplateMode ? 'copy-project-as-template-dialog' : 'copy-project-dialog')}>
        <p className={styles.description}>
          {isTemplateMode
            ? 'Copy this project into a template that can be used when creating new projects.'
            : 'Make a copy of this project that can be used as a starting point for another project.'}
        </p>

        {/* eslint-disable-next-line jsx-a11y/no-redundant-roles --
          Safari/VoiceOver doesn't announce `<ul>` elements as lists when
          `list-style: none` is set in CSS (WCAG 1.3.1). Adding `role="list"`
          restores the list semantics so AT users hear "list, N items".
         */}
        <ul className={styles.infoList} role="list">
          <CopyInfoListItem icon={<CheckIcon aria-label="Included" />} tone="success">
            Views, custom fields, insights, and workflows will be copied.
          </CopyInfoListItem>
          <CopyInfoListItem icon={<DashIcon aria-label="Not included" />} tone="muted">
            Issues and pull requests will not be copied.
          </CopyInfoListItem>
          {!isTemplateMode && (
            <li className={styles.infoListItem}>
              {/*
                FormControl auto-generates the htmlFor/id association between
                Checkbox and FormControl.Label — don't break the wrapping.
              */}
              <FormControl>
                <Checkbox
                  // Override Safari's default skipping of checkboxes in the Tab order (macOS Safari).
                  tabIndex={0}
                  checked={includeDraftIssues}
                  onChange={e => setIncludeDraftIssues(e.target.checked)}
                  {...testIdProps('copy-project-include-draft-issues')}
                />
                <FormControl.Label className={styles.draftIssuesLabel}>
                  Draft issues will be copied if selected
                </FormControl.Label>
              </FormControl>
            </li>
          )}
        </ul>

        <FormControl required className={styles.field}>
          <FormControl.Label id={OWNER_LABEL_ID}>Owner</FormControl.Label>
          <div /* Remove SelectPanel as a child of FormControl which would break the picker's anchor element */>
            <SelectPanel
              title="Owner"
              aria-label="Owner for the new project"
              open={ownerPickerOpen}
              onOpenChange={setOwnerPickerOpen}
              renderAnchor={renderOwnerAnchor}
              items={items}
              selected={selectedItem}
              onSelectedChange={handleOwnerSelectedChange}
              onFilterChange={handleFilterChange}
              filterValue={inputValue}
              placeholderText="Search owners"
              inputLabel="Search owners"
              loading={ownersQuery.isFetching && items.length === 0}
              message={ownerMessage}
              overlayProps={{width: 'medium'}}
            />
          </div>
          <VisuallyHidden id={OWNER_REQUIRED_HINT_ID}>Required</VisuallyHidden>
          {ownersQuery.isError && (
            <FormControl.Validation id={OWNER_ERROR_ID} variant="error">
              Couldn&apos;t load owners. Try again later.
            </FormControl.Validation>
          )}
        </FormControl>

        <FormControl required className={styles.field}>
          <FormControl.Label>New project name</FormControl.Label>
          <TextInput
            ref={titleInputRef}
            block
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="Name"
            {...testIdProps('copy-project-title-input')}
          />
        </FormControl>

        {errorMessage && (
          <Flash variant="danger" role="alert" {...testIdProps('copy-project-error')}>
            {errorMessage}
          </Flash>
        )}
      </div>
    </Dialog>
  )
}

const EMPTY_OWNERS: CopyProjectOwner[] = []

function buildDefaultTitle(sourceTitle: string, mode: CopyProjectDialogMode): string {
  const prefix = mode === 'template' ? TEMPLATE_TITLE_PREFIX : COPY_TITLE_PREFIX
  return `${prefix} ${sourceTitle}`
}

interface CopyInfoListItemProps {
  icon: React.ReactNode
  tone: 'success' | 'muted'
  children: React.ReactNode
}

function CopyInfoListItem({icon, tone, children}: CopyInfoListItemProps) {
  const iconClassName = clsx(styles.infoIcon, tone === 'success' ? styles.infoIconSuccess : styles.infoIconMuted)
  return (
    <li className={styles.infoListItem}>
      <span className={iconClassName}>{icon}</span>
      <span>{children}</span>
    </li>
  )
}
